import React, { useEffect, useState, useCallback } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { ProgressBar, Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import {
  FORM_SLIDE,
  EMPTY_STRING,
  authConstants,
  defaultSchedule,
  SLIDE_DIRECTION,
  SELECT_FORM_SLIDE,
} from "../../constants";
import { getAgentSignupSchema } from "../../schemas";
import {
  CloseAdminModalIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "../../assets";
import CustomSvg from "../../shared/components/custom-svg/CustomSvg";
import { authActions, countryAction } from "../../redux/actions";
import EmergencyContactForm from "./forms/EmergencyContactForm";
import AgentInformationForm from "./forms/AgentInformationForm";
import CompanyInformationForm from "./forms/CompanyInformationForm";
import CompanyOfficeHoursForm from "./forms/CompanyOfficeHoursForm";

const totalFormSlides = 4;

const SignupForm = (props) => {
  const {
    userId = null,
    allCountries,
    allLanguages,
    show,
    onClose,
    closeModal,

    getAllCountries,
    getAllLanguages,
    agentRegisteration,
    spinnerActions,

    defaultValues={}
  } = props;

  const [schedule, setSchedule] = useState(() => _.cloneDeep(defaultSchedule));
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [selectedPreferredContactMethod, setSelectedPreferredContactMethod] =
    useState(null);

  const [direction, setDirection] = useState(SLIDE_DIRECTION.NEXT);
  const [currentSlide, setCurrentSlide] = useState(
    FORM_SLIDE.COMPANY_INFORAMTION
  );

  const methods = useForm({
    defaultValues:defaultValues,
    mode: "onChange",
    resolver: yupResolver(getAgentSignupSchema(userId ? false : true)), // Apply the yup schema here
  });
  const {
    trigger,
    setValue,
    clearErrors,
    formState: { errors },
  } = methods;

  const getProgress = () => (currentSlide / totalFormSlides) * 100;

  const handleNext = async () => {
    const isValid = await trigger(SELECT_FORM_SLIDE[currentSlide]);

    if (isValid) {
      setDirection(SLIDE_DIRECTION.NEXT);
      setTimeout(() => {
        setCurrentSlide((prev) => prev + 1);
      }, 0);
    }
  };

  const handlePrevious = () => {
    setDirection(SLIDE_DIRECTION.PREVIOUS);
    setTimeout(() => {
      setCurrentSlide((prev) => prev - 1);
    }, 0);
  };

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    // eslint-disable-next-line
    [spinnerActions]
  );

  const handleRegistrationSuccess = () => {
    setSchedule({ ...defaultSchedule });
    setSelectedCountry(null);
    setSelectedLanguage([]);
    setSelectedPreferredContactMethod(null);
    closeModal();
  };

  const onSubmit = (data) => {
    const { agent, company, emergencyContactInfo, document } = data;

    let formdata = new FormData();
    // Agent info
    if (userId) {
      formdata.append("agent.customerId", userId);
    }
    formdata.append("agent.firstName", agent.firstName);
    formdata.append("agent.lastName", agent.lastName);
    formdata.append("agent.phoneNumber", agent.phoneNumber);
    formdata.append("agent.email", agent.email);
    formdata.append(
      "agent.preferredContactMethod",
      selectedPreferredContactMethod.id
    );
    formdata.append("agent.password", agent.password);
    [...selectedLanguage].forEach((lang) => {
      formdata.append("agent.languageSpokenIds", lang.id);
    });

    (emergencyContactInfo?.files || []).forEach((img, index) => {
      formdata.append(`photoId${index + 1}`, img);
    });
    (emergencyContactInfo?.headshotfiles || []).forEach((img) => {
      formdata.append(img.name, img);
    });
    [...document].forEach((img) => {
      formdata.append("document", img);
    });

    // Company info
    formdata.append("company.name", company.name);
    formdata.append("company.phoneNumber", company.phoneNumber);
    formdata.append("company.licenseNumber", company.licenseNumber);
    formdata.append("company.websiteUrl", company.websiteUrl);
    formdata.append("company.addressLine1", company.addressLine1);
    formdata.append("company.addressLine2", company.addressLine2);
    formdata.append("company.countryId", selectedCountry.id);
    formdata.append("company.state", company.stateAndProvince);
    formdata.append("company.city", company.city);

    // Company office hour info
    [...schedule].forEach((s, index) => {
      formdata.append(`company.officeHours[${index}].name`, s.name);
      formdata.append(`company.officeHours[${index}].startTime`, s.startTime);
      formdata.append(`company.officeHours[${index}].endTime`, s.endTime);
      formdata.append(`company.officeHours[${index}].isActive`, s.isActive);
    });

    agentRegisteration(formdata, handleRegistrationSuccess, !!userId);
  };

  useEffect(() => {
    getAllCountries();
    getAllLanguages();

    // eslint-disable-next-line
  }, []);

  const setSelectedCountryHandle = (option) => {
    setSelectedCountry(option);

    setValue("company.countryId", option.id);
    clearErrors("company.countryId");
  };

  const setSelectedLanguageHandle = (option) => {
    const ids = option?.map((o) => o.id);
    setSelectedLanguage(option);

    setValue("agent.languageSpokenIds", ids);
    clearErrors("agent.languageSpokenIds");
  };

  const setSelectedPreferredContactMethodHandle = (option) => {
    setSelectedPreferredContactMethod(option);

    setValue("agent.preferredContactMethodId", option.id);
    clearErrors("agent.preferredContactMethodId");
  };

  const setScheduleHandle = (value) => {
    setSchedule(value);
    setValue("officeHours", value);
  };

  const renderSlide = () => {
    switch (currentSlide) {
      case FORM_SLIDE.COMPANY_INFORAMTION:
        return (
          <CompanyInformationForm
            allCountries={allCountries}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountryHandle}
          />
        );
      case FORM_SLIDE.COMPANY_HOURS:
        return (
          <CompanyOfficeHoursForm
            schedule={schedule}
            setSchedule={setScheduleHandle}
            errors={errors}
            trigger={trigger}
          />
        );
      case FORM_SLIDE.AGENT_INFORMATION:
        return (
          <AgentInformationForm
            isDisabled={!!userId}
            allLanguages={allLanguages}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguageHandle}
            selectedPreferredContactMethod={selectedPreferredContactMethod}
            setSelectedPreferredContactMethod={
              setSelectedPreferredContactMethodHandle
            }
          />
        );
      case FORM_SLIDE.EMERGENCY_CONTACT:
        return <EmergencyContactForm />;
      default:
        return null;
    }
  };

  return (
    <Modal
      size="xl"
      centered
      show={show}
      onHide={onClose}
      backdrop="static"
      dialogClassName="agent-signup-modal"
      backdropClassName="auth-modal-backdrop register-survey-backdrop"
    >
      <CustomSvg
        src={CloseAdminModalIcon}
        className="base-modal-close"
        onClick={onClose}
      />
      <Modal.Body>
        <div className="agent-signup-container">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              id="agent-signup-form"
            >
              {currentSlide <= totalFormSlides && (
                <ProgressBar now={getProgress()} />
              )}
              <div className="agent-signup-content">
                <SwitchTransition mode="out-in">
                  <CSSTransition
                    key={currentSlide}
                    addEndListener={(node, done) =>
                      node.addEventListener("transitionend", done, false)
                    }
                    classNames={
                      direction === SLIDE_DIRECTION.NEXT
                        ? "slide-next"
                        : "slide-prev"
                    }
                    timeout={300}
                  >
                    <div>{renderSlide()}</div>
                  </CSSTransition>
                </SwitchTransition>
              </div>
            </form>
          </FormProvider>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center px-5">
          <div className="min-w-30">
            {currentSlide > 1 && currentSlide <= totalFormSlides && (
              <div
                className="slider-button rounded-circle prev text-center pointer d-flex justify-content-center align-items-center"
                onClick={handlePrevious}
              >
                <img src={ArrowLeftIcon} alt={SLIDE_DIRECTION.PREVIOUS} />
              </div>
            )}
          </div>
          <div>
            {currentSlide === totalFormSlides && (
              <button
                className="register-submit-button"
                type="submit"
                form="agent-signup-form"
                disabled={spinner(authConstants.AGENT_REGISTRATION) > -1}
              >
                {spinner(authConstants.AGENT_REGISTRATION) > -1
                  ? "Register..."
                  : "Register"}
              </button>
            )}
          </div>
          <div className="min-w-30">
            {currentSlide < totalFormSlides && (
              <div
                className="slider-button rounded-circle next text-center pointer d-flex justify-content-center align-items-center"
                onClick={handleNext}
              >
                <img src={ArrowRightIcon} alt={SLIDE_DIRECTION.NEXT} />
              </div>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  allCountries: state.countryReducer.allCountries,
  allLanguages: state.countryReducer.allLanguages,
  spinnerActions: state.systemReducer.spinnerActions,
});

const mapDispatchToProps = (dispatch) => ({
  getAllLanguages: () => dispatch(countryAction.getAllLanguages()),
  getAllCountries: () => dispatch(countryAction.getAllCountries()),
  agentRegisteration: (data, successFunc, isRequiredReLogin) =>
    dispatch(
      authActions.agentRegisteration(data, successFunc, isRequiredReLogin)
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignupForm);
